import React, { useRef, useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useSelector, useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  updateWorkOrderApi,
  updateWorkOrderEngineerPayment,
  uploadWorkOrderExpenseAttachments,
} from "../../../services/api/workOrder";
import { showToast } from "../../../store/toast/toastSlice";
import EngineerPayForm from "../../WorkOrder/Forms/EngineerPayForm";
import { UserType, WorkOrderStatusType } from "../../../utils/enum";
import { blobUrlToFile, isStatusBetween } from "../../../utils/helpers";
import { isEqual } from "lodash";

const toAmount = (value) => (value ? Number(value) : null);

const isFreelancerExpense = (expense) => expense?.addedBy?.role?.role === UserType.FREELANCER;

// What Save would actually send — built the same way from the form (redux, where the
// fields are prefixed `engineer*`) and from the work order (paymentData), so the button
// only lights up when pressing it would change something. Expenses the engineer added
// are theirs to fill in and never travel in this payload, but removing one does — it
// goes up as a deletion — so they are tracked by id rather than by their values.
const buildEngineerSnapshot = (rates, expenses) => ({
  paymentType: rates?.paymentType || "",
  currency: rates?.currency || "",
  hourlyRate: toAmount(rates?.hourlyRate),
  additionalHourlyRate: toAmount(rates?.additionalHourlyRate),
  minimumHours: toAmount(rates?.minimumHours),
  fixedAmount: toAmount(rates?.fixedAmount),
  halfDayRate: toAmount(rates?.halfDayRate),
  fullDayRate: toAmount(rates?.fullDayRate),
  fthRate: toAmount(rates?.fthRate),
  enableCustomRates: !!rates?.enableCustomRates,
  expenses: (expenses || [])
    .filter((expense) => expense.expenseType && !isFreelancerExpense(expense))
    .map((expense) => ({
      id: expense.id,
      expenseType: expense.expenseType,
      description: expense.description || "",
      amount: toAmount(expense.amount),
      attachment: expense.attachment?.fileName || null,
    })),
  freelancerExpenseIds: (expenses || []).filter(isFreelancerExpense).map((expense) => expense.id),
});

const EditEngineerRatesModal = ({ open, close, paymentData }) => {
  const dispatch = useDispatch();
  const payRef = useRef();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const id = useSelector((state) => state.workOrder.workOrderDetails.id);
  const payment = useSelector((state) => state.workOrder.payment);
  const workOrderStatus = useSelector((state) => state.workOrder.workOrderDetails.status);

  // EngineerPayForm derives the "custom rates" toggle the same two ways when it
  // hydrates: on from the stored rates, and forced on once the work order is under
  // way. Mirroring both here keeps the untouched form from reading as edited.
  const { pay } = payment;
  const hasChanges = !isEqual(
    buildEngineerSnapshot(
      {
        paymentType: pay?.engineerPaymentType,
        currency: pay?.engineerCurrency,
        hourlyRate: pay?.engineerHourlyRate,
        // Behind a checkbox, and unticking it clears the rate on save.
        additionalHourlyRate: pay?.enableEngineerAdditionalHourRate
          ? pay?.engineerAdditionalHourlyRate
          : null,
        minimumHours: pay?.engineerMinimumHours,
        fixedAmount: pay?.engineerFixedAmount,
        halfDayRate: pay?.engineerHalfDayRate,
        fullDayRate: pay?.engineerFullDayRate,
        fthRate: pay?.engineerFthRate,
        enableCustomRates: pay?.enableCustomRates,
      },
      payment?.engineerExpenses,
    ),
    buildEngineerSnapshot(
      {
        ...paymentData,
        enableCustomRates:
          isStatusBetween(
            workOrderStatus,
            WorkOrderStatusType.ASSIGNED,
            WorkOrderStatusType.INVOICED,
          ) ||
          !!(
            paymentData?.hourlyRate ||
            paymentData?.halfDayRate ||
            paymentData?.fullDayRate ||
            paymentData?.fixedAmount
          ),
      },
      paymentData?.expenses,
    ),
  );

  const { isPending: isUpdatingLoading, mutateAsync: updateWorkOrderApiMutation } = useMutation({
    mutationFn: updateWorkOrderApi,
  });
  const {
    isPending: isUpdatingEngineerPaymentLoading,
    mutateAsync: updateWorkOrderEngineerPaymentApiMutation,
  } = useMutation({
    mutationFn: updateWorkOrderEngineerPayment,
  });
  const validateEngineerPay = (pay, dispatch, navigate, isEdit = false) => {
    if (pay.enableCustomRates) {
      const {
        engineerCurrency,
        engineerPaymentType,
        engineerHourlyRate,
        engineerFullDayRate,
        engineerHalfDayRate,
        engineerFixedAmount,
        engineerMinimumHours,
        engineerAdditionalHourlyRate,
      } = pay;

      if (engineerMinimumHours > 0 && engineerAdditionalHourlyRate <= 0) {
        dispatch(showToast({ message: "Additional Hourly rate is required", type: "error" }));
        return false;
      }

      if (
        (engineerPaymentType === "HOURLY" && !engineerHourlyRate) ||
        (engineerPaymentType === "FULL_DAY" && !engineerFullDayRate) ||
        (engineerPaymentType === "HALF_DAY" && !engineerHalfDayRate)
      ) {
        dispatch(showToast({ message: "Engineer rates are required", type: "error" }));
        return false;
      }

      if (
        (!engineerCurrency || engineerCurrency === "") &&
        (engineerHourlyRate || engineerFullDayRate || engineerHalfDayRate)
      ) {
        dispatch(showToast({ message: "Engineer Currency is required", type: "error" }));
        return false;
      }

      if (engineerPaymentType === "FIXED" && (!engineerFixedAmount || engineerFixedAmount <= 0)) {
        dispatch(
          showToast({
            message: "Fixed Amount rate cannot be less than $1",
            type: "error",
          }),
        );
        return false;
      }
    }

    return true;
  };
  const formatWorkOrderExpenses = (expenses) => {
    return expenses
      ?.filter(
        (expense) =>
          expense.expenseType &&
          (!expense.addedBy || expense.addedBy.role?.role !== UserType.FREELANCER),
      )
      ?.map((expense) => {
        const { expenseType, description, amount, id, expenseKey } = expense;
        return {
          id,
          expenseType,
          description,
          amount: parseFloat(amount),
          expenseKey,
        };
      });
  };

  const uploadEngineerExpenseFiles = async (expenses) => {
    const withFiles = (expenses || []).filter(
      (e) =>
        e.attachment?.url?.startsWith("blob:") &&
        (!e.addedBy || e.addedBy.role?.role !== UserType.FREELANCER),
    );
    if (!withFiles.length) return;

    const formData = new FormData();
    formData.append(
      "expenses",
      JSON.stringify(withFiles.map((e) => ({ expenseKey: e.expenseKey }))),
    );

    for (const expense of withFiles) {
      const fileObj = await blobUrlToFile(expense.attachment.url, expense.attachment.fileName);
      const fileWithKey = new File([fileObj], `${expense.expenseKey}__-${fileObj.name}`, {
        type: fileObj.type,
      });
      formData.append("files", fileWithKey);
    }

    await uploadWorkOrderExpenseAttachments({ workOrderId: id, formData });
  };
  const handleSubmit = async () => {
    if (!payRef.current?.isValid()) return;
    setIsSaving(true);
    try {
      const { pay, engineerExpenses } = payment;
      if (!validateEngineerPay(pay, dispatch)) return;
      const deletedFreelancerExpenses = payRef.current.getDeletedExpenses();
      let payload = {
        id,
        engineerPaymentType: pay.engineerPaymentType || "",
        ...(deletedFreelancerExpenses?.length ? { deletedFreelancerExpenses } : {}),
      };

      if (pay.enableCustomRates) {
        payload = {
          ...payload,
          engineerCurrency: pay.engineerCurrency || "",
          engineerExpenses: formatWorkOrderExpenses(engineerExpenses) || [],
          engineerHourlyRate: pay.engineerHourlyRate ? Number(pay.engineerHourlyRate) : null,
          // Behind a checkbox: unticking it must clear the rate, not keep the
          // value still shown in the disabled input.
          engineerAdditionalHourlyRate:
            pay.enableEngineerAdditionalHourRate && pay.engineerAdditionalHourlyRate
              ? Number(pay.engineerAdditionalHourlyRate)
              : null,
          engineerMinimumHours: pay.engineerMinimumHours ? Number(pay.engineerMinimumHours) : null,
          engineerFixedAmount: pay.engineerFixedAmount ? Number(pay.engineerFixedAmount) : null,
          engineerHalfDayRate: pay.engineerHalfDayRate ? Number(pay.engineerHalfDayRate) : null,
          engineerFullDayRate: pay.engineerFullDayRate ? Number(pay.engineerFullDayRate) : null,
          engineerFthRate: pay.engineerFthRate ? Number(pay.engineerFthRate) : null,
        };
      } else {
        payload = {
          ...payload,
          engineerCurrency: null,
          engineerHourlyRate: null,
          engineerAdditionalHourlyRate: null,
          engineerMinimumHours: null,
          engineerFixedAmount: null,
          engineerHalfDayRate: null,
          engineerFullDayRate: null,
          engineerFthRate: null,
          engineerExpenses: formatWorkOrderExpenses(engineerExpenses) || [],
        };
      }

      if (workOrderStatus === WorkOrderStatusType.PUBLISHED) {
        await updateWorkOrderApiMutation(payload);
      } else {
        await updateWorkOrderEngineerPaymentApiMutation(payload);
      }

      await uploadEngineerExpenseFiles(engineerExpenses);

      await queryClient.invalidateQueries(["jobDetail", id]);

      dispatch(
        showToast({
          message: "Engineer rates updated successfully!",
          type: "success",
        }),
      );
      close();
    } catch (error) {
      dispatch(
        showToast({
          message: "Failed to update engineer rates.",
          type: "error",
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isEngineerPayDisabled = (workOrder) => {
    const { pay } = workOrder.payment;

    if (!pay.paymentTerms) return true;
    if (!pay.engineerPaymentType) return true;

    if (pay.enableCustomRates) {
      if (!pay.engineerCurrency) return true;

      if (pay.engineerPaymentType === "HOURLY") {
        if (!pay.engineerHourlyRate || pay.engineerHourlyRate <= 0) return true;

        if (pay.engineerMinimumHours > 0 && pay.engineerAdditionalHourlyRate <= 0) {
          return true;
        }
      }

      if (pay.engineerPaymentType === "HALF_DAY") {
        if (!pay.engineerHalfDayRate) return true;
        if (!pay.engineerHourlyRate) return true;
      }

      if (pay.engineerPaymentType === "FULL_DAY") {
        if (!pay.engineerFullDayRate) return true;
        if (!pay.engineerHourlyRate) return true;
      }

      if (pay.engineerPaymentType === "FIXED") {
        if (!pay.engineerFixedAmount || pay.engineerFixedAmount <= 0) return true;
      }
    }

    return false;
  };
  return (
    <BaseModal open={open} close={close}>
      <div className="py-7 px-12 xl:w-[1400px] lg:w-[1000px] sm:w-[800px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Engineer Rates</h6>

        <div className="mb-2 mt-3">
          <EngineerPayForm
            status={workOrderStatus}
            ref={payRef}
            isEditMode={true}
            paymentData={paymentData}
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          isLoading={isSaving}
          loaderSize={24}
          loaderColor="primary"
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || isSaving || isEngineerPayDisabled({ payment })}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditEngineerRatesModal;
